/**
 * stats.js
 */

import React from 'react';
import axios from 'axios';
import { useTheme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import {
  Box,
  Container,
  Grid,
  Link,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { showIntercomArticle } from 'utils/misc';
import useMounted from 'utils/use-mounted';
import PageTitle from 'components/page-title';
import SummaryStats from 'components/summary-stats';
import { AppBarContext } from 'contexts/app-bar/app-bar-context';


const useStyles = makeStyles(theme => ({
  root: {
    paddingTop: theme.spacing(4),
    [theme.breakpoints.down('md')]: {
      paddingTop: theme.spacing(3),
    },
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  helpLink: {
    pointerEvents: 'all',
    cursor: 'pointer',
  },
}));


export default function Stats(props) {
  const classes = useStyles();
  const isMounted = useMounted();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down('sm'), { noSsr: true });
  const { setAppBar } = React.useContext(AppBarContext);
  const [trainingStats, setTrainingStats] = React.useState();
  const [deploymentStats, setDeploymentStats] = React.useState();

  const processTrainingStats = React.useCallback((stats) => {
    return {
      recent: {
        label: stats.recent.SliceLabel,
        numberStats: [
          {
            value: stats.recent.EventCount + stats.recent.ClassesAttended,
            label: [stats.recent.EventCount + stats.recent.ClassesAttended === 1 ? 'Event / Class' : 'Events / Classes']
          },
          {
            value: stats.recent.ExerciseCount,
            label: [`Exercise${stats.recent.ExerciseCount === 1 ? '' : 's'}`]
          },
          {
            value: stats.recent.DogTrainingDuration + stats.recent.ClassesHours,
            label: [`Hour${stats.recent.DogTrainingDuration + stats.recent.ClassesHours === 1 ? '' : 's'}`]
          }
        ]
      },
      datasets: [
        {
          label: 'Hours Trained',
          backgroundColor: theme.palette.primary.colors[7],
          hoverBackgroundColor: theme.palette.primary.colors[5],
        }
      ],
      bars: stats.byMonth.slice(0, 7).map(slice => {
        return {
          label: slice.SliceLabel.split(' ')[0],
          numberStats: [
            {
              value: slice.EventCount + slice.ClassesAttended,
              label: [slice.EventCount + slice.ClassesAttended === 1 ? 'Event/Class' : 'Events/Classes']
            },
            {
              value: slice.ExerciseCount,
              label: [`Exercise${slice.ExerciseCount === 1 ? '' : 's'}`]
            },
            {
              value: slice.DogTrainingDuration + slice.ClassesHours,
              label: [`Hour${slice.DogTrainingDuration + slice.ClassesHours === 1 ? '' : 's'}`]
            }
          ],
          data: {
            'Hours Trained': slice.DogTrainingDuration + slice.ClassesHours
          },
        };
      })
    };
  }, [theme.palette.primary.colors]);

  const processDeploymentStats = React.useCallback((stats) => {
    return {
      recent: {
        label: stats.recent.SliceLabel,
        numberStats: [
          {
            value: stats.recent.TotalCount,
            label: [
              `${stats.recent.PatrolCount} Patrol`,
              `${stats.recent.DetectionCount} Detection`
            ]
          },
          {
            value: stats.recent.DetectionStats.ArrestCount + stats.recent.PatrolStats.ArrestCount,
            label: [`Arrest${stats.recent.DetectionStats.ArrestCount + stats.recent.PatrolStats.ArrestCount === 1 ? '' : 's'}`]
          },
          {
            value: stats.recent.DetectionStats.SeizureIncidentCount,
            label: [`Seizure Incident${stats.recent.DetectionStats.SeizureIncidentCount === 1 ? '' : 's'}`]
          }
        ]
      },
      datasets: [
        {
          label: 'Detection',
          backgroundColor: theme.palette.primary.colors[5],
          hoverBackgroundColor: theme.palette.primary.colors[3],
        },
        {
          label: 'Patrol',
          backgroundColor: theme.palette.primary.colors[7],
          hoverBackgroundColor: theme.palette.primary.colors[5],
        },
      ],
      bars: stats.byMonth.slice(0, 7).map(slice => {
        return {
          label: slice.SliceLabel.split(' ')[0],
          numberStats: [
            {
              value: slice.TotalCount,
              label: [
                `${slice.PatrolCount} Patrol`,
                `${slice.DetectionCount} Detection`
              ]
            },
            {
              value: slice.DetectionStats.ArrestCount + slice.PatrolStats.ArrestCount,
              label: [`Arrest${slice.DetectionStats.ArrestCount + slice.PatrolStats.ArrestCount === 1 ? '' : 's'}`]
            },
            {
              value: slice.DetectionStats.SeizureIncidentCount,
              label: [`Seizure Incident${slice.DetectionStats.SeizureIncidentCount === 1 ? '' : 's'}`]
            }
          ],
          data: {
            'Patrol': slice.PatrolCount,
            'Detection': slice.DetectionCount
          }
        };
      })
    };
  }, [theme.palette.primary.colors]);


  React.useEffect(() => {
    document.title = 'PACKTRACK - Stats';
    setAppBar('Stats', null);
  }, [setAppBar]);


  // after mount, get page details
  React.useEffect(() => {
    axios.get('/stats')
      .then((response) => {
        if (!isMounted.current) return;
        if (response.status !== 200) return;
        const data = response.data;
        if (data.trainingStats && data.deploymentStats) {
          setTrainingStats(processTrainingStats(data.trainingStats));
          setDeploymentStats(processDeploymentStats(data.deploymentStats));
        }
      });

  }, [processTrainingStats, processDeploymentStats, isMounted]);

  return (
    <Container className={classes.root}>

      {/* title */}
      {!isMobile &&
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <PageTitle iconName='stats' title='Stats' />
        </Box>
      }

      <Grid container spacing={3} justifyContent="space-between" direction="row">
        <Grid item xs={12}>
          <Typography variant='subtext-large'>
            <>Review basic stats on your training and deployment progress. For a more detailed breakdown, generate a </>
            <Link
              className={`${classes.helpLink}`}
              onClick={() => showIntercomArticle(6685221, isMobile)}
              children={<>Training or Deployment Summary Report</>}
              underline="hover" 
            />
            <> from the Records page. </>
            <Link
              className={`${classes.helpLink}`}
              onClick={() => showIntercomArticle(6716555, isMobile)}
              children={<>Learn How</>}
              underline="hover" 
            />
            .
          </Typography>
        </Grid>

        {/* training summary stats */}
        <Grid item xs={12} sm={6}>
          <SummaryStats
            title="Training Summary"
            chartLabel="Hours Trained"
            toolTipSuffix="Hour"
            data={trainingStats}
            yStepSize={0.5}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <SummaryStats
            title="Deployment Summary"
            chartLabel="Total Deployments"
            toolTipSuffix="Deployment"
            data={deploymentStats}
            yStepSize={1}
          />
        </Grid>

      </Grid>
    </Container>
  );
}